import { PAYMENT_METHOD_LABELS, type InvoiceDetail } from '@faktorplus/shared';
import { InvoiceStateBadge } from '../../components/ui/status-pill';
import { projectLabel } from './invoice-labels';
import { formatDate, formatMoney } from '../../lib/format';
import styles from './invoice-detail.module.css';

/** The identity of the invoice: one dominant number, then the facts behind it. */
export function InvoiceSummary({ invoice }: { invoice: InvoiceDetail }) {
  const allocation = invoice.allocations[0];
  const hasRetention = invoice.retentionAmount > 0;
  const corrections = invoice.adjustments.filter(
    (adjustment) => adjustment.kind === 'CORRECTION',
  );

  return (
    <section className={styles.summary}>
      <div className={styles.summaryHead}>
        <div>
          <h1 className={styles.vendor}>{invoice.vendorName}</h1>
          <p className={styles.docNumber}>{invoice.number}</p>
        </div>
        <InvoiceStateBadge invoice={invoice} />
      </div>

      <p className={styles.amount}>{formatMoney(invoice.grossAmount)}</p>
      <p className={styles.amountNote}>
        brutto · netto {formatMoney(invoice.netAmount)}
        {hasRetention && ` · do zapłaty ${formatMoney(invoice.amountDue)}`}
      </p>

      <dl className={styles.facts}>
        <Fact label="Budowa" value={projectLabel(invoice).text.replace('— ', '')} />
        <Fact label="Kategoria" value={invoice.categoryName ?? '—'} />
        <Fact label="Kierownik" value={allocation?.managerName ?? '—'} />
        <Fact label="Data faktury" value={formatDate(invoice.issueDate)} />
        <Fact label="Termin płatności" value={formatDate(invoice.dueDate)} />
        {invoice.requiresHandoverProtocol && (
          <Fact label="Protokół odbioru" value={formatProtocol(invoice)} />
        )}
        {hasRetention && (
          <Fact label="Zatrzymanie" value={formatMoney(invoice.retentionAmount)} />
        )}
        {/* Only worth a line when it deviates from the norm; the bookkeeping
            card is where the current setting always is. */}
        {invoice.paymentMethod === 'CASH' && (
          <Fact label="Forma płatności" value={PAYMENT_METHOD_LABELS.CASH} />
        )}
        {invoice.employees.length > 0 && (
          <Fact
            label={invoice.employees.length === 1 ? 'Pracownik' : 'Pracownicy'}
            value={invoice.employees.map((employee) => employee.fullName).join(', ')}
          />
        )}
        {invoice.ledgerRef && <Fact label="Pozycja księgowa" value={invoice.ledgerRef} />}
        {invoice.paidAt && <Fact label="Zapłacono" value={formatDate(invoice.paidAt)} />}
        {/* Some transfers in, the rest still owed — the running balance. */}
        {!invoice.paidAt && invoice.paidAmount > 0 && (
          <Fact
            label="Wpłacono"
            value={`${formatMoney(invoice.paidAmount)} · zostało ${formatMoney(invoice.remainingAmount)}`}
          />
        )}
        {/* The permanent record of a release made without the owner: the badge
            disappears once they sign off, this line never does. */}
        {invoice.approvedForPaymentWithoutOwner && (
          <Fact label="Akceptacja właściciela" value={formatOwnerApproval(invoice)} />
        )}
        {/* Correction linkage, both ways: what this document corrects, and
            which corrections point at it (24.09.2026). */}
        {invoice.correctsNumber && (
          <Fact label="Koryguje fakturę" value={invoice.correctsNumber} />
        )}
        {invoice.correctedBy.length > 0 && (
          <Fact
            label="Faktury korygujące"
            value={invoice.correctedBy.map((correction) => correction.number).join(', ')}
          />
        )}
        {corrections.length > 0 && (
          <Fact label="Korekty rozliczone tutaj" value={formatCorrections(corrections)} />
        )}
      </dl>

      {invoice.notes && <p className={styles.notes}>{invoice.notes}</p>}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}

/**
 * The annotation the client keeps by hand today: which correction moved this
 * amount, and by how much. Adjustment sign flipped back into the delta —
 * a lowering correction reads as the minus it is.
 */
function formatCorrections(corrections: InvoiceDetail['adjustments']): string {
  return corrections
    .map(
      (adjustment) =>
        `${adjustment.documentNumber ?? 'korekta'} (${formatMoney(-adjustment.amount)})`,
    )
    .join(', ');
}

/** "brak" while it hangs over the owner; who and when once they signed off. */
function formatOwnerApproval(invoice: InvoiceDetail): string {
  if (!invoice.ownerApprovedAt) return 'brak — czeka na potwierdzenie';

  const who = invoice.ownerApprovedByName ? ` (${invoice.ownerApprovedByName})` : '';
  return `potwierdzona ${formatDate(invoice.ownerApprovedAt)}${who}`;
}

/**
 * The protocol used to replace the payment term with "czeka na protokół",
 * which left nobody able to say whether that term had passed. It gets its own
 * line instead: the term is a date, the protocol is a separate fact.
 */
function formatProtocol(invoice: InvoiceDetail): string {
  if (!invoice.documentsCompleteAt) return 'brak';
  return `dostarczony ${formatDate(invoice.documentsCompleteAt)}`;
}
